import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowRight, Megaphone, Layout, Search, Users, Settings, MessageCircle,
  TrendingUp, DollarSign, Globe, Award, ChevronRight, CheckCircle2,
  Star, Quote, Zap, Target, BarChart3, Heart
} from "lucide-react";
import { fadeUp, stagger, AnimatedSection } from "@/lib/animations";

/* ===== HERO SECTION ===== */
function HeroSection() {
  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="hero-glow absolute inset-0 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-32">
        <AnimatedSection className="max-w-4xl mx-auto text-center">
          <motion.div variants={fadeUp}>
            <Badge className="bg-[#D85A30]/10 text-[#D85A30] border-[#D85A30]/20 hover:bg-[#D85A30]/15 mb-6 px-4 py-1.5 rounded-full" data-testid="hero-badge">
              <span className="kicker">Per Nonprofit e Fondazioni</span>
            </Badge>
          </motion.div>

          <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C2C2A] leading-[1.1] mb-6">
            La tua missione merita<br />
            <span className="text-[#D85A30]">di essere trovata.</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-base md:text-lg text-[#888780] max-w-2xl mx-auto mb-10 leading-relaxed">
            Aiutiamo le nonprofit a ottenere fino a <strong className="text-[#2C2C2A]">120.000€/anno in fondi per il marketing e per le AI</strong>, 
            costruire siti web ad alta conversione e amplificare la presenza digitale — così più persone trovano e sostengono la tua causa.
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link to="/book-a-call">
              <Button className="rounded-full px-8 h-12 text-base bg-[#1D9E75] hover:bg-[#17845F] text-white font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-1" data-testid="hero-cta-primary">
                Ottieni il Tuo Piano Strategico Gratuito
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/results">
              <Button variant="outline" className="rounded-full px-8 h-12 text-base border-[#085041]/20 text-[#2C2C2A] hover:bg-[#085041]/5 font-medium" data-testid="hero-cta-secondary">
                Scopri i Nostri Risultati
              </Button>
            </Link>
          </motion.div>

          {/* Stats row */}
          <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-3xl mx-auto">
            {[
              { value: "15M€+", label: "Fondi Gestiti", icon: DollarSign },
              { value: "500+", label: "Nonprofit Servite", icon: Heart },
              { value: "300%", label: "Crescita Media Traffico", icon: TrendingUp },
              { value: "4.9/5", label: "Valutazione Clienti", icon: Star },
            ].map((stat) => (
              <div key={stat.label} className="text-center" data-testid={`hero-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-center mb-1">
                  <stat.icon className="w-4 h-4 text-[#D85A30] mr-1.5" />
                  <span className="text-2xl md:text-3xl font-bold text-[#2C2C2A]" style={{ fontFamily: "'Fraunces', serif" }}>
                    {stat.value}
                  </span>
                </div>
                <span className="text-xs md:text-sm text-[#888780]">{stat.label}</span>
              </div>
            ))}
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ===== TRUST BAR ===== */
function TrustBar() {
  const orgs = [
    "World Wildlife Fund", "Habitat for Humanity", "Red Cross Chapter",
    "Boys & Girls Club", "Salvation Army", "United Way",
    "YMCA", "Feeding America", "Goodwill", "Big Brothers Big Sisters"
  ];
  return (
    <section className="py-12 border-y border-[#D9D8D2]/50 bg-[#F1EFE8]/30 overflow-hidden" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <p className="text-center text-sm text-[#888780] kicker">Scelti da oltre 500 nonprofit in tutta Italia</p>
      </div>
      <div className="relative">
        <div className="flex animate-scroll-left" style={{ width: "200%" }}>
          {[...orgs, ...orgs].map((org, i) => (
            <div key={i} className="flex items-center justify-center min-w-[200px] px-8">
              <div className="flex items-center gap-2 opacity-40 hover:opacity-70 transition-opacity">
                <Globe className="w-5 h-5 text-[#2C2C2A]" />
                <span className="text-sm font-medium text-[#2C2C2A] whitespace-nowrap">{org}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===== PROBLEM SECTION ===== */
function ProblemSection() {
  const problems = [
    {
      icon: Globe,
      title: "Invisibili online quando le persone cercano la tua causa",
      description: "Se la tua nonprofit non è visibile online, potenziali donatori e volontari troveranno qualcun altro. Il 75% delle persone non va mai oltre la prima pagina.",
    },
    {
      icon: DollarSign,
      title: "120.000€/anno in fondi marketing lasciati sul tavolo",
      description: "La maggior parte delle nonprofit si qualifica per i fondi per il marketing e per le AI da 10.000€/mese ma non sa nemmeno che esistono. Sono 120.000€ all'anno in pubblicità gratuita, non reclamata.",
    },
    {
      icon: Layout,
      title: "Sito web obsoleto che allontana i sostenitori",
      description: "Il tuo sito web è spesso la prima impressione. Se sembra costruito nel 2010, i visitatori se ne vanno prima di donare o fare volontariato.",
    },
  ];

  return (
    <section className="py-24 md:py-32" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="max-w-3xl mb-16">
          <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">Il Problema</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2C2C2A] leading-tight">
            La tua nonprofit ha una missione.{" "}
            <span className="text-[#888780]">Non lasciare che la tua presenza online la ostacoli.</span>
          </motion.h2>
        </AnimatedSection>

        <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {problems.map((item, i) => (
            <motion.div key={i} variants={fadeUp}>
              <Card className="bg-white border-[#D9D8D2]/50 shadow-[0_10px_30px_-10px_rgba(8,80,65,0.05)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-300 h-full group" data-testid={`problem-card-${i}`}>
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-2xl bg-[#D85A30]/10 flex items-center justify-center mb-6 group-hover:bg-[#D85A30]/20 transition-colors">
                    <item.icon className="w-6 h-6 text-[#D85A30]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#2C2C2A] mb-3 leading-snug">{item.title}</h3>
                  <p className="text-sm text-[#888780] leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ===== SERVICES SECTION (BENTO GRID) ===== */
function ServicesSection() {
  const services = [
    {
      icon: Megaphone, title: "Fondi per il Marketing e per le AI", tag: "Più Popolare",
      desc: "10.000€/mese in fondi marketing gestiti per la tua nonprofit. Sono 120.000€/anno in pubblicità gratuita.",
      features: ["Domanda fondi", "Gestione campagne", "Report mensili", "Ottimizzazione parole chiave"],
      span: "md:col-span-2"
    },
    {
      icon: Settings, title: "Soluzioni CRM Tutto in Uno",
      desc: "Gestisci donatori, volontari e stakeholder con gli strumenti e i flussi di lavoro giusti.",
      features: ["Setup CRM", "Migrazione dati", "Automazione", "Formazione"],
      span: "md:col-span-1"
    },
    {
      icon: Layout, title: "Creazione Siti Web",
      desc: "Siti web moderni e mobile-first progettati per convertire i visitatori in donatori e volontari.",
      features: ["Design responsive", "Integrazione donazioni", "Gestione eventi", "Predisposto SEO"],
      span: "md:col-span-1"
    },
    {
      icon: Search, title: "SEO e Contenuti",
      desc: "Fatti trovare quando i donatori cercano la tua causa. Ottimizziamo tutta la tua presenza digitale.",
      features: ["SEO on-page", "Strategia contenuti", "Ricerca locale", "Analytics"],
      span: "md:col-span-1"
    },
    {
      icon: Users, title: "Costruzione Comunità",
      desc: "Trasforma i donatori occasionali in sostenitori a vita con comunità online coinvolgenti.",
      features: ["Setup piattaforma", "Strategia engagement", "Crescita membri", "Calendario editoriale"],
      span: "md:col-span-1"
    },
    {
      icon: MessageCircle, title: "Consulenza",
      desc: "Guida strategica per aiutare la tua organizzazione a prendere le migliori decisioni digitali.",
      features: ["Audit digitale", "Roadmap crescita", "Revisione tech stack", "Supporto continuo"],
      span: "md:col-span-2 lg:col-span-1"
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#F1EFE8]/40" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">Cosa Facciamo</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2C2C2A] leading-tight mb-4">
            Tutto ciò che serve alla tua nonprofit<br className="hidden sm:block" /> per crescere online
          </motion.h2>
          <motion.p variants={fadeUp} className="text-base md:text-lg text-[#888780] max-w-2xl mx-auto">
            Sei servizi collaudati, una missione: aiutare la tua organizzazione a raggiungere più persone e massimizzare il tuo impatto.
          </motion.p>
        </AnimatedSection>

        <AnimatedSection className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, i) => (
            <motion.div key={i} variants={fadeUp} className={service.span}>
              <Card className="bg-white border-[#D9D8D2]/50 shadow-[0_10px_30px_-10px_rgba(8,80,65,0.05)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-300 h-full group cursor-pointer relative overflow-hidden" data-testid={`service-card-${i}`}>
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-[#085041]/5 flex items-center justify-center group-hover:bg-[#085041]/10 transition-colors">
                      <service.icon className="w-6 h-6 text-[#2C2C2A]" />
                    </div>
                    {service.tag && (
                      <Badge className="bg-[#D85A30] text-white border-none text-xs rounded-full" data-testid="popular-badge">{service.tag}</Badge>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-[#2C2C2A] mb-2">{service.title}</h3>
                  <p className="text-sm text-[#888780] leading-relaxed mb-5">{service.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.features.map((f, j) => (
                      <span key={j} className="flex items-center gap-1 text-xs text-[#2C2C2A]/70 bg-[#085041]/5 px-2.5 py-1 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> {f}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ===== HOW IT WORKS ===== */
function ProcessSection() {
  const steps = [
    {
      num: "01", title: "Revisione Strategica Gratuita",
      desc: "Analizziamo l'intera presenza digitale della tua nonprofit e ti mostriamo esattamente dove sono le lacune.",
      icon: Target,
    },
    {
      num: "02", title: "Il Tuo Piano Personalizzato",
      desc: "Creiamo una strategia su misura basata sulle esigenze e sugli obiettivi unici della tua organizzazione.",
      icon: BarChart3,
    },
    {
      num: "03", title: "Lo Realizziamo Noi",
      desc: "Il nostro team esegue il piano — progettando, costruendo e ottimizzando tutto per te.",
      icon: Zap,
    },
    {
      num: "04", title: "Guarda Crescere il Tuo Impatto",
      desc: "Più visitatori, più donatori, più volontari. I report mensili dimostrano il ROI.",
      icon: TrendingUp,
    },
  ];

  return (
    <section className="py-24 md:py-32" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">Come Funziona</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2C2C2A] leading-tight mb-4">
            Il tuo percorso verso un impatto maggiore
          </motion.h2>
          <motion.p variants={fadeUp} className="text-base md:text-lg text-[#888780] max-w-2xl mx-auto">
            Ogni organizzazione è diversa, quindi ogni piano è diverso. Ecco come ci arriviamo insieme.
          </motion.p>
        </AnimatedSection>

        <AnimatedSection className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <motion.div key={i} variants={fadeUp}>
              <div className="relative p-8 rounded-2xl bg-white border border-[#D9D8D2]/50 shadow-[0_10px_30px_-10px_rgba(8,80,65,0.05)] h-full group hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-300" data-testid={`process-step-${i}`}>
                <span className="text-5xl font-bold text-[#2C2C2A]/5 absolute top-4 right-6" style={{ fontFamily: "'Fraunces', serif" }}>
                  {step.num}
                </span>
                <div className="w-12 h-12 rounded-2xl bg-[#D85A30]/10 flex items-center justify-center mb-6 group-hover:bg-[#D85A30]/20 transition-colors">
                  <step.icon className="w-6 h-6 text-[#D85A30]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2C2C2A] mb-2">{step.title}</h3>
                <p className="text-sm text-[#888780] leading-relaxed">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ===== TESTIMONIALS ===== */
function TestimonialsSection() {
  const testimonials = [
    {
      quote: "Givenda ha trasformato la nostra presenza online. Siamo passati da 200 visitatori al mese a oltre 4.000. Solo i fondi marketing hanno portato 28.000€ in pubblicità gratuita.",
      author: "Sara Bianchi",
      role: "Direttrice Esecutiva, Fondazione Speranza",
      metric: "+1.900% traffico web",
    },
    {
      quote: "In 6 mesi siamo costantemente il primo risultato di ricerca per le nonprofit nella nostra zona. Abbiamo iniziato a ricevere donatori che ci hanno trovato online!",
      author: "Marco Verdi",
      role: "Direttore di Programma, Alleanza Terra Verde",
      metric: "#1 ricerca locale",
    },
    {
      quote: "Non avevamo idea di poter accedere a fondi marketing gratuiti. Givenda ha gestito tutto e ora raggiungiamo migliaia di potenziali sostenitori ogni mese.",
      author: "Lisa Rossi",
      role: "Fondatrice, Zampe & Amore",
      metric: "10K€/mese ads gratis",
    },
    {
      quote: "Il nuovo sito web e l'hub comunitario hanno cambiato tutto per noi. Le persone restano, interagiscono e donano. È stata una svolta per la nostra missione.",
      author: "Giuseppe Ferrari",
      role: "Direttore, Fondazione Futuro Giovani",
      metric: "2x iscrizioni volontari",
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#F1EFE8]/40" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">Testimonianze</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2C2C2A] leading-tight mb-4">
            Le nonprofit adorano lavorare con noi.{" "}
            <span className="text-[#888780]">Ecco perché.</span>
          </motion.h2>
        </AnimatedSection>

        <AnimatedSection className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.map((t, i) => (
            <motion.div key={i} variants={fadeUp}>
              <Card className="bg-white border-[#D9D8D2]/50 shadow-[0_10px_30px_-10px_rgba(8,80,65,0.05)] h-full" data-testid={`testimonial-card-${i}`}>
                <CardContent className="p-8">
                  <Quote className="w-8 h-8 text-[#D85A30]/20 mb-4" />
                  <p className="text-[#2C2C2A] leading-relaxed mb-6 text-base">{t.quote}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-[#2C2C2A] text-sm">{t.author}</p>
                      <p className="text-xs text-[#888780]">{t.role}</p>
                    </div>
                    <Badge className="bg-[#085041]/5 text-[#2C2C2A] border-none text-xs rounded-full hover:bg-[#085041]/10">{t.metric}</Badge>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ===== WHY US / COMPARISON ===== */
function WhyUsSection() {
  const features = [
    "Specialisti nel no-profit",
    "Esperti fondi per il marketing e per le AI",
    "15M€+ in fondi gestiti",
    "Team dedicato",
    "Report ROI mensili",
    "Sito + SEO + Ads sotto un unico tetto",
  ];

  return (
    <section className="py-24 md:py-32" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">Perché Givenda</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2C2C2A] leading-tight mb-6">
                Non siamo solo marketer.{" "}
                <span className="text-[#888780]">Siamo dalla tua parte.</span>
              </motion.h2>
              <motion.p variants={fadeUp} className="text-base text-[#888780] leading-relaxed mb-8">
                La maggior parte delle agenzie vede un cliente. Noi vediamo un'organizzazione con una missione 
                per cambiare il mondo. Questa differenza cambia tutto nel nostro modo di lavorare.
              </motion.p>
              <motion.div variants={fadeUp}>
                <Link to="/book-a-call">
                  <Button className="rounded-full px-8 h-12 bg-[#1D9E75] hover:bg-[#17845F] text-white font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-1" data-testid="why-us-cta">
                    Inizia la Tua Revisione Gratuita
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </motion.div>
            </div>

            <motion.div variants={fadeUp}>
              <div className="bg-white rounded-2xl border border-[#D9D8D2]/50 shadow-[0_10px_30px_-10px_rgba(8,80,65,0.05)] overflow-hidden" data-testid="comparison-card">
                <div className="grid grid-cols-3 bg-[#085041] text-white text-sm font-medium">
                  <div className="p-4">Caratteristica</div>
                  <div className="p-4 text-center">Givenda</div>
                  <div className="p-4 text-center">Altri</div>
                </div>
                {features.map((f, i) => (
                  <div key={i} className={`grid grid-cols-3 text-sm ${i % 2 === 0 ? "bg-[#F1EFE8]/30" : "bg-white"}`}>
                    <div className="p-4 text-[#2C2C2A] font-medium">{f}</div>
                    <div className="p-4 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-[#2C2C2A]" />
                    </div>
                    <div className="p-4 flex items-center justify-center">
                      <span className="w-5 h-5 rounded-full border-2 border-[#D9D8D2]" />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ===== FAQ SECTION ===== */
function FAQSection() {
  const faqs = [
    {
      q: "Quanto costa?",
      a: "I nostri servizi sono personalizzati per ogni organizzazione. La gestione dei fondi per il marketing e per le AI parte da 397€/mese. I siti web partono da 97€/mese. Molte nonprofit compensano i costi interamente attraverso i fondi marketing (10.000€/mese in pubblicità gratuita). Ti illustreremo tutte le opzioni nella chiamata strategica gratuita."
    },
    {
      q: "Siamo una piccola nonprofit. Fa per noi?",
      a: "Assolutamente sì. Lavoriamo con organizzazioni di ogni dimensione, da team di 10 persone a grandi fondazioni. Di fatto, le nonprofit più piccole spesso vedono la crescita più significativa perché c'è molto potenziale non sfruttato."
    },
    {
      q: "Ci qualifichiamo davvero per 120.000€ l'anno in fondi marketing gratuiti?",
      a: "La maggior parte delle organizzazioni no-profit registrate si qualifica per i fondi per il marketing e per le AI. Gestiamo l'intero processo di domanda e la gestione continuativa delle campagne. Oltre il 90% delle organizzazioni con cui lavoriamo viene approvato."
    },
    {
      q: "Quanto tempo ci vuole per vedere risultati?",
      a: "La maggior parte delle organizzazioni vede miglioramenti misurabili entro 60-90 giorni. I siti web possono essere pronti in 2-4 settimane. I risultati SEO diventano tipicamente significativi intorno al mese 3-4. Le campagne di fondi marketing iniziano a generare traffico entro 2-3 settimane dall'approvazione."
    },
    {
      q: "E se non siamo soddisfatti dei risultati?",
      a: "Garantiamo il nostro lavoro. Se in qualsiasi momento non vedi i risultati di cui abbiamo discusso, lavoreremo con te per adattare la strategia. Il nostro alto tasso di fidelizzazione dei clienti dimostra che il nostro approccio funziona costantemente."
    },
    {
      q: "Servono competenze tecniche?",
      a: "Assolutamente no. Gestiamo tutto l'aspetto tecnico — design, sviluppo, SEO, campagne pubblicitarie e setup della comunità. Tutto ciò di cui abbiamo bisogno è la tua passione per la tua missione. Al resto pensiamo noi."
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#F1EFE8]/40" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">FAQ</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2C2C2A] leading-tight">
            Domande frequenti dalle nonprofit
          </motion.h2>
        </AnimatedSection>

        <AnimatedSection className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp}>
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {faqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="bg-white rounded-2xl border border-[#D9D8D2]/50 px-6 shadow-sm"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-[#2C2C2A] font-semibold text-left text-base hover:no-underline py-5">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#888780] leading-relaxed text-sm pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ===== FINAL CTA ===== */
function FinalCTA() {
  return (
    <section className="py-24 md:py-32 bg-[#085041] relative overflow-hidden" data-testid="final-cta-section">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#D85A30] blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-[#D85A30] blur-[100px]" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection className="text-center max-w-3xl mx-auto">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-tight mb-6">
            Pronto ad amplificare{" "}
            <span className="text-[#D85A30]">la tua missione?</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-base md:text-lg text-white/60 mb-10 leading-relaxed">
            Ottieni una revisione gratuita e senza impegno della presenza online della tua nonprofit. 
            Ti mostreremo esattamente dove ti trovi e cosa fare dopo.
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <Link to="/book-a-call">
              <Button className="rounded-full px-8 h-12 text-base bg-[#D85A30] hover:bg-[#C04E25] text-white font-medium shadow-lg whitespace-nowrap transition-all hover:-translate-y-0.5" data-testid="cta-book-call-button">
                Prenota una Chiamata Gratuita
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <a href="mailto:hello@givenda.com">
              <Button variant="outline" className="rounded-full px-8 h-12 text-base border-white/20 text-white hover:bg-white/10 font-medium" data-testid="cta-email-button">
                Scrivici un'Email
              </Button>
            </a>
          </motion.div>
          <motion.div variants={fadeUp}>
            <div className="flex items-center justify-center gap-6 text-sm text-white/40">
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> 100% gratuito</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Nessuna carta di credito</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Risultati in 24 ore</span>
            </div>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ===== HOME PAGE ===== */
export default function HomePage() {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <TrustBar />
      <ProblemSection />
      <ServicesSection />
      <ProcessSection />
      <TestimonialsSection />
      <WhyUsSection />
      <FAQSection />
      <FinalCTA />
    </div>
  );
}
